import { useEffect, useState } from 'react';
import { Loader2, Sparkles, Globe } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTime } from '@/lib/dateFormat';

// UTM / campaign attribution as a timeline of "arrivals": the lead's own
// first submission (first touch) plus every later duplicate submission that
// was merged into it — each keeps its own source / UTM / ad data.

interface Arrival {
  id: string;
  createdAt: string;
  source: string | null;
  mergeSource?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
  campaignName?: string | null;
  campaignId?: string | null;
  adName?: string | null;
  adId?: string | null;
  formId?: string | null;
  leadgenId?: string | null;
  fbclid?: string | null;
}

const MERGE_SOURCE_LABEL: Record<string, string> = {
  manual_form: 'Manual entry',
  csv_import: 'CSV import',
  api: 'API',
  webhook: 'Webhook',
};

// "Name · id" when both exist, else whichever exists.
const withId = (name?: string | null, id?: string | null) =>
  name && id ? <>{name} <span className="font-mono text-[11px] text-slate-400">· {id}</span></> : (name || (id ? <span className="font-mono text-xs">{id}</span> : null));

function ArrivalCard({ a, first }: { a: Arrival; first: boolean }) {
  const label = a.source || (a.mergeSource ? MERGE_SOURCE_LABEL[a.mergeSource] ?? a.mergeSource : null) || 'Unknown source';
  const rows: [string, React.ReactNode][] = ([
    ['Lead source', a.source],
    ['UTM source', a.utmSource],
    ['UTM medium', a.utmMedium],
    ['Campaign', withId(a.campaignName, a.campaignId)],
    ['UTM campaign', a.utmCampaign && a.utmCampaign !== a.campaignName ? a.utmCampaign : null],
    ['Ad', withId(a.adName, a.adId)],
    ['UTM term', a.utmTerm],
    ['UTM content', a.utmContent],
    ['Form', a.formId ? <span className="font-mono text-xs">{a.formId}</span> : null],
    ['Lead id', a.leadgenId ? <span className="font-mono text-xs">{a.leadgenId}</span> : null],
    ['Click id (fbclid)', a.fbclid ? <span className="font-mono text-xs break-all">{a.fbclid}</span> : null],
  ] as [string, React.ReactNode][]).filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div className="relative pl-6">
      <span className={`absolute left-0 top-5 h-3 w-3 rounded-full ${first ? 'bg-[#6366F1]' : 'bg-indigo-300'}`} />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="inline-flex rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">{label}</span>
            {first && <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">First touch</span>}
          </div>
          <span className="text-xs text-slate-500">{formatDateTime(a.createdAt)}</span>
        </div>
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Campaign (UTM)</p>
        {rows.length === 0 ? (
          <p className="mt-1 text-sm text-slate-400">No campaign or UTM data on this arrival.</p>
        ) : (
          <dl className="mt-2 grid max-w-2xl grid-cols-[96px_minmax(0,1fr)] sm:grid-cols-[140px_minmax(0,1fr)] gap-x-3 sm:gap-x-4 gap-y-1.5 rounded-lg border border-slate-100 p-3 text-sm">
            {rows.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-slate-500">{k}</dt>
                <dd className="min-w-0 text-slate-800 [overflow-wrap:anywhere]">{v}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}

export function LeadUtmTab({ lead }: { lead: any }) {
  const [dups, setDups] = useState<Arrival[]>([]);
  const [loading, setLoading] = useState(false);

  // Later arrivals live as duplicates merged into this lead.
  useEffect(() => {
    if (!(lead?.duplicateCount > 0)) { setDups([]); return; }
    setLoading(true);
    api.get(`/leads/${lead.id}/duplicates`)
      .then(({ data }) => setDups(Array.isArray(data?.duplicates) ? data.duplicates : []))
      .catch(() => setDups([]))
      .finally(() => setLoading(false));
  }, [lead?.id, lead?.duplicateCount]);

  const first: Arrival = {
    id: lead.id, createdAt: lead.createdAt, source: lead.source ?? null,
    utmSource: lead.utmSource, utmMedium: lead.utmMedium, utmCampaign: lead.utmCampaign,
    utmTerm: lead.utmTerm, utmContent: lead.utmContent, campaignName: lead.campaignName,
    campaignId: lead.campaignId, adName: lead.adName, adId: lead.adId, formId: lead.formId,
    leadgenId: lead.leadgenId, fbclid: lead.fbclid,
  };
  const arrivals = [first, ...dups].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const campaigns = new Set(arrivals.map(a => a.campaignName || a.utmCampaign).filter(Boolean));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-slate-400" /> {arrivals.length} arrival{arrivals.length !== 1 ? 's' : ''}</span>
        <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4 text-slate-400" /> {campaigns.size} campaign{campaigns.size !== 1 ? 's' : ''}</span>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <div className="relative space-y-3">
        <span className="absolute left-[5px] top-6 bottom-6 w-px bg-slate-200" />
        {arrivals.map(a => <ArrivalCard key={a.id} a={a} first={a.id === arrivals[0].id} />)}
      </div>
    </div>
  );
}
